import { useEffect, useState } from "react";
import {
  Alert,
  App,
  Button,
  Card,
  Form,
  Input,
  Space,
  Tabs,
  Typography,
} from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ADMIN_ROLES } from "@glow-price/shared";
import type { HomeBanner, HomeFeature, LegalDocument } from "@glow-price/shared";

import { homeSettingsApi, legalDocsApi, type LegalDocKey } from "../api/resources";
import { useCurrentAdmin } from "../auth/useCurrentAdmin";

/**
 * Phase 6D-2 系统设置 / site_settings 管理。
 *
 * TODO(api): 后端目前仅暴露各个 site_setting 的 typed endpoint
 *   (/api/admin/home-settings/{feature,banners}, /api/admin/legal/{privacy,terms})；
 * 没有通用的 `/api/admin/site-settings?key=` 或 list 接口可枚举所有 key。
 * 在 generic endpoint 落地前，本页面对每个已知 key 提供一个 JSON 编辑器，
 * 复用既有 typed endpoint 读写。新增的 key 加 tab 即可。
 */

type JsonTabConfig<T> = {
  key: string;
  label: string;
  description: string;
  load: () => Promise<T>;
  save: (value: T) => Promise<unknown>;
};

const HOME_FEATURE_TAB: JsonTabConfig<HomeFeature> = {
  key: "home_feature",
  label: "home_feature",
  description: "首页编辑精选大图卡（典型字段：enabled / eyebrow / title / body / cta / linkTo / imageUrl）。",
  load: () => homeSettingsApi.getFeature(),
  save: (value) => homeSettingsApi.putFeature(value),
};

const HOME_BANNERS_TAB: JsonTabConfig<{ items: HomeBanner[] }> = {
  key: "home_banners",
  label: "home_banners",
  description: "首页顶部 3 张 Banner（数组，每项含 title / sub / linkTo）。",
  load: () => homeSettingsApi.getBanners(),
  save: (value) => homeSettingsApi.putBanners(value.items),
};

type LegalSavePayload = Pick<
  LegalDocument,
  "version" | "title" | "bodyMarkdown" | "requireReconsent"
>;
const LEGAL_PRIVACY_TAB: JsonTabConfig<LegalDocument> = {
  key: "legal_privacy",
  label: "legal_privacy",
  description: "隐私政策文档（version / title / bodyMarkdown / requireReconsent）。",
  load: () => legalDocsApi.get("privacy"),
  save: (value) =>
    legalDocsApi.put("privacy", pickLegalSavePayload(value)),
};
const LEGAL_TERMS_TAB: JsonTabConfig<LegalDocument> = {
  key: "legal_terms",
  label: "legal_terms",
  description: "服务条款文档（version / title / bodyMarkdown / requireReconsent）。",
  load: () => legalDocsApi.get("terms"),
  save: (value) =>
    legalDocsApi.put("terms", pickLegalSavePayload(value)),
};

function pickLegalSavePayload(value: LegalDocument): LegalSavePayload {
  return {
    version: value.version,
    title: value.title,
    bodyMarkdown: value.bodyMarkdown,
    requireReconsent: value.requireReconsent,
  };
}

const TABS: JsonTabConfig<unknown>[] = [
  HOME_FEATURE_TAB as JsonTabConfig<unknown>,
  HOME_BANNERS_TAB as JsonTabConfig<unknown>,
  LEGAL_PRIVACY_TAB as JsonTabConfig<unknown>,
  LEGAL_TERMS_TAB as JsonTabConfig<unknown>,
];

const _LEGAL_KEY_TO_DOCKEY: Record<string, LegalDocKey> = {
  legal_privacy: "privacy",
  legal_terms: "terms",
};
// (Reserved for future generic site_settings PUT mapping.)
void _LEGAL_KEY_TO_DOCKEY;

export function SystemSettingsPage() {
  const me = useCurrentAdmin();
  const allowed = !!me && (me.role === ADMIN_ROLES.SUPERADMIN || me.role === ADMIN_ROLES.EDITOR);

  if (!allowed) {
    return (
      <Alert
        type="error"
        showIcon
        message="无权访问"
        description="系统设置仅对超级管理员（superadmin）与编辑（editor）开放。"
      />
    );
  }

  return (
    <div>
      <Typography.Title level={4} style={{ marginTop: 0 }}>
        系统设置（site_settings）
      </Typography.Title>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="原始 JSON 编辑"
        description="每个 tab 对应一个 site_settings key。表单实际写入仍走该 key 的 typed endpoint，所以 schema 必须满足后端校验。"
      />
      <Tabs
        defaultActiveKey={TABS[0]!.key}
        items={TABS.map((t) => ({
          key: t.key,
          label: t.label,
          children: <JsonTabPanel config={t} />,
        }))}
      />
    </div>
  );
}

function JsonTabPanel<T>({ config }: { config: JsonTabConfig<T> }) {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [form] = Form.useForm<{ json: string }>();
  const [parseError, setParseError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["site-settings", config.key],
    queryFn: () => config.load(),
  });

  useEffect(() => {
    if (query.data !== undefined) {
      form.setFieldsValue({ json: JSON.stringify(query.data, null, 2) });
      setParseError(null);
    }
  }, [query.data, form]);

  const mutation = useMutation({
    mutationFn: (value: T) => Promise.resolve(config.save(value)),
    onSuccess: () => {
      message.success(`${config.key} 已保存`);
      qc.invalidateQueries({ queryKey: ["site-settings", config.key] });
    },
    onError: (err: Error) => message.error(err.message),
  });

  const handleSave = () => {
    const text = form.getFieldValue("json") as string;
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
      setParseError(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setParseError(msg);
      message.error(`JSON 解析失败：${msg}`);
      return;
    }
    mutation.mutate(parsed as T);
  };

  const handleFormat = () => {
    const text = form.getFieldValue("json") as string;
    try {
      const parsed = JSON.parse(text);
      form.setFieldsValue({ json: JSON.stringify(parsed, null, 2) });
      setParseError(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setParseError(msg);
      message.error(`JSON 解析失败：${msg}`);
    }
  };

  return (
    <Card loading={query.isLoading}>
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        {config.description}
      </Typography.Paragraph>
      <Form<{ json: string }> form={form} layout="vertical">
        <Form.Item
          name="json"
          label="JSON 内容"
          validateStatus={parseError ? "error" : undefined}
          help={parseError ?? undefined}
        >
          <Input.TextArea
            rows={18}
            style={{ fontFamily: "monospace", fontSize: 12 }}
            placeholder="{}"
          />
        </Form.Item>
      </Form>
      <Space>
        <Button type="primary" loading={mutation.isPending} onClick={handleSave}>
          保存
        </Button>
        <Button onClick={handleFormat}>格式化</Button>
        <Button
          onClick={() => {
            if (query.data !== undefined) {
              form.setFieldsValue({ json: JSON.stringify(query.data, null, 2) });
              setParseError(null);
            }
          }}
        >
          重置
        </Button>
      </Space>
    </Card>
  );
}
